import { useState, useEffect, useRef } from 'react';
import { FiX, FiSearch, FiBook } from 'react-icons/fi';
import { LIBROS_BIBLIA } from '../../utils/bibliaHelper';

/**
 * Componente de navegación rápida
 * Permite buscar y navegar rápidamente a cualquier libro/capítulo/versículo
 */
export default function QuickNavigation({ onNavigate, onClose, currentBook, currentChapter }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBook, setSelectedBook] = useState(currentBook || 'Genesis');
  const [chapter, setChapter] = useState(currentChapter || 1);
  const [verse, setVerse] = useState('');
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [showBookList, setShowBookList] = useState(false);
  const searchInputRef = useRef(null);

  // Lista única de libros
  const uniqueBooks = Object.values(LIBROS_BIBLIA)
    .filter((value, index, self) => self.indexOf(value) === index);

  useEffect(() => {
    // Focus en el input al abrir
    searchInputRef.current?.focus();
  }, []);

  useEffect(() => {
    // Filtrar libros según búsqueda
    if (searchTerm) {
      const filtered = uniqueBooks.filter(book =>
        book.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredBooks(filtered);
      setShowBookList(true);
    } else {
      setFilteredBooks([]);
      setShowBookList(false);
    }
  }, [searchTerm]);

  const handleBookSelect = (book) => {
    setSelectedBook(book);
    setSearchTerm('');
    setShowBookList(false);
    setChapter(1);
    setVerse('');
  };

  const handleNavigate = () => {
    const verseNum = verse ? parseInt(verse) : null;
    onNavigate(selectedBook, chapter, verseNum);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (showBookList && filteredBooks.length > 0) {
        handleBookSelect(filteredBooks[0]);
      } else {
        handleNavigate();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Libros organizados por categoría
  const booksByCategory = {
    'Pentateuco': ['Génesis', 'Éxodo', 'Levítico', 'Números', 'Deuteronomio'],
    'Históricos': ['Josué', 'Jueces', 'Rut', '1 Samuel', '2 Samuel', '1 Reyes', '2 Reyes', '1 Crónicas', '2 Crónicas', 'Esdras', 'Nehemías', 'Ester'],
    'Poéticos': ['Job', 'Salmos', 'Proverbios', 'Eclesiastés', 'Cantar de los Cantares'],
    'Profetas Mayores': ['Isaías', 'Jeremías', 'Lamentaciones', 'Ezequiel', 'Daniel'],
    'Profetas Menores': ['Oseas', 'Joel', 'Amós', 'Abdías', 'Jonás', 'Miqueas', 'Nahúm', 'Habacuc', 'Sofonías', 'Hageo', 'Zacarías', 'Malaquías'],
    'Evangelios': ['Mateo', 'Marcos', 'Lucas', 'Juan'],
    'Historia NT': ['Hechos'],
    'Epístolas Paulinas': ['Romanos', '1 Corintios', '2 Corintios', 'Gálatas', 'Efesios', 'Filipenses', 'Colosenses', '1 Tesalonicenses', '2 Tesalonicenses', '1 Timoteo', '2 Timoteo', 'Tito', 'Filemón'],
    'Epístolas Generales': ['Hebreos', 'Santiago', '1 Pedro', '2 Pedro', '1 Juan', '2 Juan', '3 Juan', 'Judas'],
    'Profecía': ['Apocalipsis']
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-20 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden shadow-2xl animate-slide-down"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-500 rounded-lg">
                <FiSearch className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-2xl font-bold">
                  Navegación Rápida
                </h3>
                <p className="text-sm text-indigo-100">
                  Busca cualquier libro, capítulo o versículo
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-indigo-500 rounded-lg transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>

          {/* Búsqueda de libro */}
          <div className="relative">
            <div className="relative">
              <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Buscar libro... (ej: Genesis, Juan, Salmos)"
                className="w-full pl-12 pr-4 py-3 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-indigo-300"
              />
            </div>

            {/* Lista de libros filtrados */}
            {showBookList && filteredBooks.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl max-h-60 overflow-y-auto z-10">
                {filteredBooks.map((book) => (
                  <button
                    key={book}
                    onClick={() => handleBookSelect(book)}
                    className="w-full text-left px-4 py-3 hover:bg-indigo-50 transition-colors border-b last:border-b-0"
                  >
                    <div className="flex items-center gap-3">
                      <FiBook className="text-indigo-600" />
                      <span className="font-medium text-gray-900">{book}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Contenido */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-250px)]">
          {/* Selección Actual */}
          <div className="mb-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
            <p className="text-sm font-medium text-indigo-900 mb-2">
              Referencia Seleccionada:
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-2xl font-bold text-indigo-600">
                {selectedBook}
              </span>
              <span className="text-2xl text-indigo-400">•</span>
              <span className="text-2xl font-bold text-indigo-600">
                Capítulo {chapter}
              </span>
              {verse && (
                <>
                  <span className="text-2xl text-indigo-400">:</span>
                  <span className="text-2xl font-bold text-indigo-600">
                    {verse}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Controles de Capítulo y Versículo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Capítulo
              </label>
              <input
                type="number"
                value={chapter}
                onChange={(e) => setChapter(parseInt(e.target.value) || 1)}
                onKeyDown={handleKeyPress}
                min="1"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Versículo (opcional)
              </label>
              <input
                type="number"
                value={verse}
                onChange={(e) => setVerse(e.target.value)}
                onKeyDown={handleKeyPress}
                min="1"
                placeholder="Ej: 16"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-lg"
              />
            </div>
          </div>

          {/* Libros por Categoría */}
          <div className="space-y-4">
            <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
              Seleccionar por Categoría
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(booksByCategory).map(([category, books]) => (
                <div key={category} className="border rounded-lg p-3">
                  <h5 className="font-semibold text-indigo-900 mb-2 text-sm">
                    {category}
                  </h5>
                  <div className="flex flex-wrap gap-1">
                    {books.map((book) => (
                      <button
                        key={book}
                        onClick={() => handleBookSelect(book)}
                        className={`px-2 py-1 text-xs rounded transition-colors ${
                          selectedBook === book
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-indigo-100'
                        }`}
                      >
                        {book}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Presiona <kbd className="px-2 py-1 bg-gray-200 rounded text-xs">Enter</kbd> para ir
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleNavigate}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
            >
              Ir a {selectedBook} {chapter}{verse && `:${verse}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}





